import {
  createAsyncThunk,
  createSlice,
  createSelector
} from '@reduxjs/toolkit';
import BN from 'bn.js';
import { set } from 'lodash';

import { createParameterSelector } from 'redux/selectors/topLevel';
import FungibleTokens, { fungibleTokensService } from 'services/fungible-tokens';
import { tabsOptions } from 'lib/constants';
import { listProjects } from 'services/indexer';
import { actions as tokenFiatValueActions } from 'redux/slices/tokenFiatValues';
import { TokenTypes } from 'types';
import { getProjectIdFromToken } from 'utils';

import tokensMetadataSlice, {
  getCachedContractMetadataOrFetch,
  selectContractsMetadata
} from '../tokensMetadata';

import config from 'config';
const { OPN_CONTRACT, OCC_CONTRACT,USDC_CONTRACT, METHODS } = config;

const initialPlanting = {
  last_reward_id: null,
  number_of_rewards: '0',
  planting_amount: '0'
};

const initialWithdraw = {
  uncollected_rewards: [],
  uncollected_amount: '0',
  collected_rewards: [],
  collected_amount: '0',
};

const initialState: TokenTypes = {
  lockedBalance: '0',
  ownedTokens: {
    [OPN_CONTRACT]: {
      balance: '0'
    },
    [OCC_CONTRACT]: {
      balance: '0'
    },
        [USDC_CONTRACT]: {
        balance: '0'
}
  },
  projects: [],
  planting: { ...initialPlanting },
  withdraw: { ...initialWithdraw },
  activeTab: tabsOptions[0],
  activeToken: null
};

const initialOwnedTokenState = {
  balance: ''
};

type iKeys = {
  balance: string;
};

const SLICE_NAME = 'tokens';

const fetchOwnedTokensForContract = createAsyncThunk(
  `${SLICE_NAME}/fetchOwnedTokensForContract`,
  async (
    {
      accountId,
      contractName
    }: {
      accountId: string;
      contractName: string;
    },
    thunkAPI
  ) => {
    const {
      actions: { addLockedBalance, addTokensMetadata }
    } = tokensSlice;
    const { dispatch } = thunkAPI;
    const isUSDC = contractName === USDC_CONTRACT
    const balance = await FungibleTokens.getViewMethod({
      contractName,
      accountId,
      methodName:isUSDC?METHODS.ft_balance_of: METHODS.get_profile
    });
    if (contractName === OPN_CONTRACT) {
      dispatch(
        addTokensMetadata({
          accountId,
          contractName,
          balance: balance?.unlocked_balance
        })
      );

      dispatch(
        addLockedBalance({
          lockedBalance: balance?.locked_balance
        })
      );
    }
    if (isUSDC) {
      dispatch(
          addTokensMetadata({
            accountId,
            contractName,
            balance: balance
          })
      );
    }
    if (contractName === OCC_CONTRACT) {
      dispatch(addTokensMetadata({
        accountId,
        contractName,
        balance: balance?.ft_total_supply
      }));
    }
  }
);

const tokensSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setActiveTab(state, action) {
      state.activeTab = action.payload;
    },
    setActiveToken(state, action) {
      state.activeToken = action.payload;
    },
    addTokensMetadata(state, { payload }) {
      const { contractName, balance } = payload;
      set(state, ['ownedTokens', contractName, 'balance'], balance);
    },
    addTokensProjects(state, { payload }) {
      const { projects } = payload;
      set(state, 'projects', projects);
    },
    addLockedBalance(state, { payload }) {
      const { lockedBalance } = payload;
      set(state, 'lockedBalance', lockedBalance);
    },
    setUserPlanting(state, { payload }) {
      const { planting } = payload;
      set(state, 'planting', planting);
    },
    setUserWithdraw(state, { payload }) {
      const { withdraw } = payload;
      set(state, 'withdraw', withdraw);
    }
  }
});

export default tokensSlice;

const fetchProjects = createAsyncThunk(
  `${SLICE_NAME}/fetchProjectsForContract`,
  async ({ accountId }: { accountId: string }, thunkAPI) => {
    const {
      actions: { addTokensProjects }
    } = tokensSlice;
    const { dispatch } = thunkAPI;

    const ftBalances = await FungibleTokens.getFtBalances({ accountId });

    const { result } = await listProjects(ftBalances);

    if (result?.hits?.hits && result.hits.hits.length > 0) {
      const projects = result?.hits?.hits.map((hit) => {
        for (const [key, value] of ftBalances) {
          if (hit._id === getProjectIdFromToken(key)) {
            return {
              ...hit._source,
              balance: +value,
              value: '',
              error: ''
            };
          }
        }
      });

      if (projects && projects.length > 0) {
        dispatch(addTokensProjects({ projects }));
      }
    }
  }
);

const fetchPlantTokens = createAsyncThunk(
  `${SLICE_NAME}/fetchPlantsTokens`,
  async (
    { accountId }: { accountId: string; },
    thunkAPI
  ) => {
    const {
      actions: { setUserPlanting, setUserWithdraw }
    } = tokensSlice;
    const { dispatch } = thunkAPI;

    const planting = await fungibleTokensService.getPlantingProfile(accountId);
    const lastRewardId = planting?.last_reward_id || null;

    const withdraw = await fungibleTokensService.getAllUncollectedPlantingRewards(accountId, lastRewardId);

    const collectedRewards = await fungibleTokensService.getAllCollectedPlantingRewards(accountId);

    await dispatch(setUserWithdraw({ withdraw: { ...withdraw, ...collectedRewards } }));

    await dispatch(setUserPlanting({ planting: { ...planting } }));
  }
);

const fetchTokens = createAsyncThunk(
  `${SLICE_NAME}/fetchTokens`,
  async (
    {
      accountId
    }: {
      accountId: string;
    },
    thunkAPI
  ) => {
    const { dispatch } = thunkAPI;

    await Promise.all(
      [OPN_CONTRACT, OCC_CONTRACT,USDC_CONTRACT].map(async (contractName) => {
        const {
          actions: { setContractMetadata }
        } = tokensMetadataSlice;

        try {
          const contractMetadata = await getCachedContractMetadataOrFetch(contractName);
          // if (!selectOneContractMetadata(getState(), { contractName })) {
          dispatch(
            setContractMetadata({
              contractName,
              metadata: {
                ...contractMetadata,
                decimals: +contractMetadata.decimals,
                symbol: contractMetadata.symbol.split('-')[0]
              }
            })
          );
          // }

            await dispatch(
            fetchOwnedTokensForContract({
              accountId,
              contractName
            })
          );

          if (contractName === OCC_CONTRACT) {
            await dispatch(
              fetchProjects({ accountId })
            );
          }
        } catch (e) {
          console.warn(`Failed to load FT for ${contractName}`, e);
        }
      })
    );
    dispatch(tokenFiatValueActions.fetchTokenFiatValues());
  }
);

export const actions = {
  fetchTokens,
  fetchProjects,
  fetchPlantTokens,
  fetchOwnedTokensForContract,
  ...tokensSlice.actions
};
export const selectTokensSlice = (state: any) => {
  return state[SLICE_NAME];
}
const selectOwnedTokens = createSelector(
  selectTokensSlice,
  ({ ownedTokens }) => ownedTokens || {}
);

const getContractNameParam = createParameterSelector(
  (params) => params.contractName
);

export const selectOneTokenProjects = createSelector(
  selectTokensSlice,
  ({ projects }) => projects || []
);

export const selectOneTokenFromOwnedTokens = createSelector(
  [selectOwnedTokens, getContractNameParam],
  (ownedTokens, contractName) =>
    ownedTokens[contractName] || initialOwnedTokenState
);

export const selectTokenBalance = createSelector(
  selectOneTokenFromOwnedTokens,
  ({ balance }) => balance || ''
);

export const selectPlanting = createSelector(
  selectTokensSlice,
    ({ planting }) => {
      return planting || initialPlanting
    }
);

export const selectWithdraw = createSelector(
  selectTokensSlice,
  ({ withdraw }) => withdraw || initialWithdraw
);

export const selectPlantedOPN = createSelector(
  selectPlanting,
  ({ planting_amount }) => planting_amount
);

export const selectPlantedLockedOPN = createSelector(
    selectPlanting,
    ({ locked_amount }) => locked_amount
);
export const selectPlantedUnLockedOPN = createSelector(
    selectPlanting,
    ({ unlocked_amount }) => unlocked_amount
);
export const selectPlantedWithdrawAt = createSelector(
    selectPlanting,
    ({ withdraw_at }) => withdraw_at
);

export const selectCollectAvailable = createSelector(
  selectWithdraw,
  ({ uncollected_amount }) => {
    return new BN(uncollected_amount).toString();
  }
);

export const selectRewardsAvailable = createSelector(
  selectWithdraw,
  ({ collected_amount }) => {
    return new BN(collected_amount).toString();
  }
);

export const selectLockedBalance = createSelector(
  selectTokensSlice,
  ({ lockedBalance }) => lockedBalance || '0'
);

export const selectTotalBalance = createSelector(
  [selectTokenBalance, selectLockedBalance],
  (available, locked) => {
   return new BN(available).add(new BN(locked)).toString()
  },
);

export const selectActiveTab = createSelector(
  selectTokensSlice,
  ({ activeTab }) => activeTab
);

export const selectActiveToken = createSelector(
  selectTokensSlice,
  ({ activeToken }) => activeToken
);

export const selectTokensWithMetadataForAccountId = createSelector(
  [selectContractsMetadata, selectOwnedTokens],
  (allContractMetadata, ownedTokensForAccount) => {
    const tokenEntries = Object.entries<iKeys>(ownedTokensForAccount);
    return tokenEntries.map(([contractName, { balance }]) => {
      return {
        ...initialOwnedTokenState,
        contractName,
        balance,
        onChainFTMetadata: allContractMetadata[contractName] || {},
        fiatValueMetadata: { usd: '0' }
      };
    });
  }
);
